/**
 * Preferences — the signed-in user's own settings (Zone 4).
 *
 * Every section is backed by a real endpoint:
 *
 * | Section          | Endpoint                                              | Req          |
 * |------------------|-------------------------------------------------------|--------------|
 * | Display          | theme (local, per device)                             | Zone 4       |
 * | Password         | `POST /auth/v1/password/reset/request`                | Zone 4       |
 * | Two-factor       | `POST /auth/v1/mfa/enroll` + `/mfa/verify`, `PATCH /auth/v1/account/mfa` | FR-AUTH-03 |
 * | Sign-in methods  | `GET`/`DELETE /auth/v1/account/identities`            | FR-AUTH-05   |
 * | Active sessions  | `GET`/`DELETE /auth/v1/sessions`                      | FR-AUTH-04   |
 *
 * Password changes reuse the verified reset-email flow on purpose: the auth
 * service has no authenticated change-password endpoint, and a mailed
 * single-use link is the same treatment a forgotten password gets — one code
 * path, verified either way.
 */
import { useEffect, useState } from 'react';

import {
  THEME_CHOICES,
  THEME_LABELS,
  usePreferences,
  type ThemeChoice,
} from '../../app/preferences-context';
import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DeskSection } from '../../components/desk-section';
import { PageHeader } from '../../components/page-header';
import { StatusPill } from '../../components/status-pill';
import { TextField } from '../../components/text-field';
import { UnsavedChangesGuard } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import type {
  LinkedIdentity,
  MfaEnrollment,
  MfaSettings,
  SessionResource,
} from '../../lib/api-types';
import { deviceLabel } from '../../lib/device-name';
import { MFA_RECOMMENDED_PERSONAS } from '../../lib/personas';

const TOTP_CODE_LENGTH = 6;

function toMessage(error: unknown, fallback: string): string {
  return ApiError.isApiError(error) ? error.message : fallback;
}

function formatWhen(iso: string | null): string {
  if (iso === null) return 'unknown';
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? 'unknown' : parsed.toLocaleString();
}

export function PreferencesPage(): JSX.Element {
  return (
    <div className="team-settings prefs" data-testid="preferences-page">
      <PageHeader
        title="Preferences"
        context="Settings that follow you, not the team: how the app looks on this device, and how your account stays secure."
      />
      <div className="desk-grid prefs__grid">
        <DisplaySection />
        <PasswordSection />
        <TwoFactorSection />
        <SignInMethodsSection />
        <SessionsSection />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- display

function DisplaySection(): JSX.Element {
  const { theme, setTheme } = usePreferences();

  return (
    <DeskSection title="Display" className="prefs__panel" data-testid="prefs-display">
      <div className="desk-form">
        <div className="field">
          <label className="field__label" htmlFor="theme-choice">
            Theme
          </label>
          <select
            className="field__control prefs__select"
            id="theme-choice"
            onChange={(event) => setTheme(event.target.value as ThemeChoice)}
            value={theme}
          >
            {THEME_CHOICES.map((choice) => (
              <option key={choice} value={choice}>
                {THEME_LABELS[choice]}
              </option>
            ))}
          </select>
          <span className="field__hint">
            Auto follows this device’s light/dark and high-contrast settings.
          </span>
        </div>
      </div>
    </DeskSection>
  );
}

// ------------------------------------------------------------------ password

function PasswordSection(): JSX.Element {
  const apiClient = useApiClient();
  const { claims, session } = useSession();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const email = claims?.email ?? null;
  const tenantId = claims?.tid ?? session?.tenantId ?? null;

  const send = async (): Promise<void> => {
    if (email === null || tenantId === null) return;
    setIsBusy(true);
    setError(null);
    try {
      await apiClient.requestPasswordReset({ tenantId, email });
      setSent(true);
    } catch (cause) {
      setError(toMessage(cause, 'We could not send the reset email. Please try again.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <DeskSection title="Password" className="prefs__panel" data-testid="prefs-password">
      <p className="muted">
        We change passwords through a verified email link rather than a form on this page — the link
        goes to <strong>{email ?? 'your account email'}</strong>, works once, and signs out your
        other devices when used.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}
      {sent ? (
        <p className="banner banner--success" role="status">
          Reset link sent. Check your email — the link is valid for a couple of hours.
        </p>
      ) : (
        <div>
          <button
            className="btn btn--secondary"
            disabled={isBusy || email === null || tenantId === null}
            onClick={() => void send()}
            type="button"
          >
            {isBusy ? 'Sending…' : 'Email me a password reset link'}
          </button>
        </div>
      )}
    </DeskSection>
  );
}

// ---------------------------------------------------------------- two-factor

function TwoFactorSection(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { session, persona, applyMfaVerification } = useSession();
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [settings, setSettings] = useState<MfaSettings | null>(null);
  const [code, setCode] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isDisableOpen, setIsDisableOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // MFA is opt-in per account: the server's flags, not the persona, say where
  // this account stands. The session's enrolment bit is the fallback until the
  // settings load.
  const isEnrolled = settings?.mfa_enrolled ?? session?.isMfaEnrolled === true;
  const isEnforced = settings?.mfa_enforced ?? isEnrolled;
  const isRecommended =
    settings?.mfa_recommended ?? (persona !== null && MFA_RECOMMENDED_PERSONAS.has(persona));

  useEffect(() => {
    const controller = new AbortController();
    apiClient
      .getMfaSettings(controller.signal)
      .then(setSettings)
      .catch(() => undefined);
    return () => controller.abort();
  }, [apiClient]);

  const start = async (): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      setEnrollment(await apiClient.enrollMfa());
    } catch (cause) {
      setError(toMessage(cause, 'We could not start two-factor enrolment.'));
    } finally {
      setIsBusy(false);
    }
  };

  const verify = async (): Promise<void> => {
    const trimmed = code.trim();
    if (trimmed.length !== TOTP_CODE_LENGTH || !/^\d+$/u.test(trimmed)) {
      setFieldError(`Enter the ${TOTP_CODE_LENGTH}-digit code from your authenticator app.`);
      return;
    }
    setIsBusy(true);
    setFieldError(null);
    try {
      applyMfaVerification(await apiClient.verifyMfa(trimmed));
      setEnrollment(null);
      setCode('');
      // Finishing enrolment IS the opt-in: sign-in asks for a code from now on.
      setSettings((current) =>
        current === null ? current : { ...current, mfa_enrolled: true, mfa_enforced: true }
      );
      showToast('Two-factor authentication is on. Sign-in will ask for a code.', 'success');
    } catch (cause) {
      setFieldError(toMessage(cause, 'That code was not accepted. Try the next one.'));
    } finally {
      setIsBusy(false);
    }
  };

  /** Turn enforcement back on for an account that kept its authenticator. */
  const requireAtSignIn = async (): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      const updated = await apiClient.updateMfaSettings({ enforced: true });
      applyMfaVerification(updated);
      setSettings(updated);
      showToast('Sign-in will ask for your authenticator code.', 'success');
    } catch (cause) {
      setError(toMessage(cause, 'We could not turn two-factor on.'));
    } finally {
      setIsBusy(false);
    }
  };

  /** Remove the authenticator + backup codes; re-authenticates with the password. */
  const disable = async (): Promise<void> => {
    setIsBusy(true);
    setPasswordError(null);
    try {
      const updated = await apiClient.updateMfaSettings({
        enrolled: false,
        ...(currentPassword === '' ? {} : { current_password: currentPassword }),
      });
      applyMfaVerification(updated);
      setSettings(updated);
      setIsDisableOpen(false);
      setCurrentPassword('');
      showToast('Two-factor authentication is off.', 'success');
    } catch (cause) {
      setPasswordError(toMessage(cause, 'We could not turn two-factor off.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <DeskSection
      title="Two-factor authentication"
      className="prefs__panel"
      data-testid="prefs-two-factor"
      actions={
        <StatusPill tone={isEnforced ? 'success' : 'neutral'}>
          {isEnforced ? 'On' : 'Off'}
        </StatusPill>
      }
    >
      <p className="muted">
        {isRecommended
          ? 'Optional, and strongly recommended for admin and coaching accounts. Turning it on asks for a 6-digit code from your authenticator app when you sign in.'
          : 'Optional for your account. Turning it on asks for a 6-digit code from your authenticator app when you sign in.'}
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}

      {/* Mid-enrollment is unsaved work: leaving now discards the TOTP secret
          and the backup codes before they were confirmed. */}
      <UnsavedChangesGuard
        isDirty={enrollment !== null && !isBusy}
        message="Two-factor setup is not finished. Leaving now discards this QR secret and these backup codes — you would have to start over."
      />
      {enrollment === null ? (
        <div className="prefs__actions">
          <button
            className="btn btn--secondary"
            disabled={isBusy}
            onClick={() => void start()}
            type="button"
          >
            {isBusy
              ? 'Working…'
              : isEnrolled
                ? 'Replace authenticator app'
                : 'Set up two-factor authentication'}
          </button>
          {isEnrolled && !isEnforced ? (
            <button
              className="btn btn--primary"
              disabled={isBusy}
              onClick={() => void requireAtSignIn()}
              type="button"
            >
              Require a code at sign-in
            </button>
          ) : null}
          {isEnrolled ? (
            <button
              className="btn btn--ghost prefs__quiet-danger"
              disabled={isBusy}
              onClick={() => {
                setPasswordError(null);
                setIsDisableOpen(true);
              }}
              type="button"
            >
              Turn off two-factor
            </button>
          ) : null}
        </div>
      ) : (
        <div className="team-settings__form prefs__enroll" data-testid="mfa-enrollment">
          <ol className="prefs__steps">
            <li className="prefs__step">
              <h3 className="prefs__step-title">Add the key to your authenticator app</h3>
              <p className="muted">
                Scan or paste this key into Google Authenticator, 1Password, Authy or any TOTP app.
              </p>
              <p className="prefs__secret mono">{enrollment.totp_uri}</p>
            </li>
            <li className="prefs__step">
              <h3 className="prefs__step-title">Save your backup codes</h3>
              <p className="muted">
                Each works once and is the only self-service way back in if you lose the
                authenticator. Keep them somewhere safe.
              </p>
              <ul className="prefs__codes">
                {enrollment.backup_codes.map((backupCode) => (
                  <li className="mono prefs__code" key={backupCode}>
                    {backupCode}
                  </li>
                ))}
              </ul>
            </li>
            <li className="prefs__step">
              <h3 className="prefs__step-title">Confirm with a code</h3>
              <p className="muted">Enter the 6-digit code your app shows right now.</p>
            </li>
          </ol>
          <form
            className="prefs__verify"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void verify();
            }}
          >
            <TextField
              autoComplete="one-time-code"
              error={fieldError}
              inputMode="numeric"
              label="6-digit code"
              name="totp-code"
              onChange={(event) => setCode(event.target.value)}
              value={code}
            />
            <div className="prefs__actions">
              <button className="btn btn--primary" disabled={isBusy} type="submit">
                {isBusy ? 'Checking…' : 'Confirm and turn on'}
              </button>
              <button
                className="btn btn--ghost"
                onClick={() => {
                  setEnrollment(null);
                  setFieldError(null);
                }}
                type="button"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {isDisableOpen ? (
        <form
          className="team-settings__form"
          aria-label="Turn off two-factor authentication"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void disable();
          }}
        >
          <p className="muted">
            This removes your authenticator app and backup codes from the account. Confirm your
            password to continue.
          </p>
          <TextField
            autoComplete="current-password"
            error={passwordError}
            label="Current password"
            name="mfa-current-password"
            onChange={(event) => setCurrentPassword(event.target.value)}
            type="password"
            value={currentPassword}
          />
          <div className="prefs__actions">
            <button className="btn btn--danger" disabled={isBusy} type="submit">
              {isBusy ? 'Turning off…' : 'Turn off two-factor'}
            </button>
            <button
              className="btn btn--ghost"
              onClick={() => {
                setIsDisableOpen(false);
                setCurrentPassword('');
                setPasswordError(null);
              }}
              type="button"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </DeskSection>
  );
}

// ------------------------------------------------------------ sign-in methods

function SignInMethodsSection(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [identities, setIdentities] = useState<LinkedIdentity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<LinkedIdentity | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    void (async () => {
      try {
        const page = await apiClient.listLinkedIdentities(controller.signal);
        setIdentities(page.data);
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(toMessage(cause, 'We could not load your sign-in methods.'));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [apiClient, reloadToken]);

  const unlink = async (identity: LinkedIdentity): Promise<void> => {
    try {
      await apiClient.unlinkIdentity(identity.identity_id);
      showToast(`${identity.provider} unlinked.`, 'success');
      setPending(null);
      setDialogError(null);
      setReloadToken((token) => token + 1);
    } catch (cause) {
      setDialogError(toMessage(cause, 'We could not unlink that provider.'));
    }
  };

  return (
    <DeskSection
      title="Sign-in methods"
      className="prefs__panel"
      data-testid="prefs-sign-in-methods"
    >
      <p className="muted">
        Signing in with any linked provider lands on this same account. To add one, sign in with it
        using this same email address and it links automatically.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}
      {isLoading ? (
        <p className="muted" role="status">
          Loading sign-in methods…
        </p>
      ) : null}
      {!isLoading && error === null && identities.length === 0 ? (
        <p className="muted">No social sign-in linked — you sign in with email and password.</p>
      ) : null}
      {identities.length > 0 ? (
        <ul className="prefs__list">
          {identities.map((identity) => (
            <li className="prefs__row" key={identity.identity_id}>
              <div className="prefs__row-main">
                <span className="prefs__row-name">{identity.provider}</span>
                <span className="prefs__row-meta">
                  {identity.email ?? 'no email on file'} · linked{' '}
                  {formatWhen(identity.linked_at).split(',')[0]}
                </span>
              </div>
              <button
                className="btn btn--ghost btn--sm prefs__quiet-danger"
                disabled={!identity.can_unlink}
                onClick={() => setPending(identity)}
                title={
                  identity.can_unlink
                    ? undefined
                    : 'This is your only way to sign in, so it cannot be unlinked.'
                }
                type="button"
              >
                Unlink
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {pending !== null ? (
        <ConfirmDialog
          confirmLabel="Unlink provider"
          consequence={`You will no longer be able to sign in with ${pending.provider}.`}
          error={dialogError}
          onCancel={() => {
            setPending(null);
            setDialogError(null);
          }}
          onConfirm={() => unlink(pending)}
          title={`Unlink ${pending.provider}?`}
          tone="danger"
        />
      ) : null}
    </DeskSection>
  );
}

// ------------------------------------------------------------------- sessions

function SessionsSection(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [sessions, setSessions] = useState<SessionResource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<SessionResource | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    void (async () => {
      try {
        const page = await apiClient.listSessions({ status: 'active' }, controller.signal);
        setSessions(page.data);
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(toMessage(cause, 'We could not load your active sessions.'));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [apiClient, reloadToken]);

  const revoke = async (target: SessionResource): Promise<void> => {
    try {
      await apiClient.revokeSession(target.session_id);
      showToast('Session signed out.', 'success');
      setPending(null);
      setDialogError(null);
      setReloadToken((token) => token + 1);
    } catch (cause) {
      setDialogError(toMessage(cause, 'We could not sign that session out.'));
    }
  };

  return (
    <DeskSection
      title="Where you’re signed in"
      className="prefs__panel prefs__panel--wide"
      data-testid="prefs-active-sessions"
    >
      <p className="muted">
        Every device with an active session for your account. If you see one you don’t recognise,
        sign it out and change your password.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}
      {isLoading ? (
        <p className="muted" role="status">
          Loading sessions…
        </p>
      ) : null}
      {!isLoading && error === null && sessions.length === 0 ? (
        <p className="muted">No active sessions found.</p>
      ) : null}
      {sessions.length > 0 ? (
        <ul className="prefs__list">
          {sessions.map((row) => (
            <li className="prefs__row prefs__row--session" key={row.session_id}>
              <div className="prefs__row-main">
                <span className="prefs__row-name" data-testid={`session-device-${row.session_id}`}>
                  {/* Always a friendly name: the server stores one, and a
                      legacy raw user agent is re-described here. */}
                  {deviceLabel(row.device, row.user_agent)}
                  {row.current ? (
                    <span className="prefs__badge prefs__badge--on"> This device</span>
                  ) : null}
                </span>
                <span className="prefs__row-meta">
                  {row.ip_address ?? 'unknown network'} · last active {formatWhen(row.last_seen)} ·
                  signed in {formatWhen(row.created_at)}
                </span>
                {row.user_agent ? (
                  <details className="prefs__row-details">
                    <summary className="prefs__row-details-summary">Browser details</summary>
                    <code className="prefs__row-ua">{row.user_agent}</code>
                  </details>
                ) : null}
              </div>
              {row.current ? null : (
                <button
                  className="btn btn--ghost btn--sm prefs__quiet-danger"
                  onClick={() => setPending(row)}
                  type="button"
                >
                  Sign out
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {pending !== null ? (
        <ConfirmDialog
          confirmLabel="Sign out session"
          consequence="That device will need to sign in again to use Gridiron Tracker."
          error={dialogError}
          onCancel={() => {
            setPending(null);
            setDialogError(null);
          }}
          onConfirm={() => revoke(pending)}
          title="Sign this session out?"
          tone="danger"
        />
      ) : null}
    </DeskSection>
  );
}
